import type { CheckBoxTreeModelType } from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { isFilterTypeGuard } from '@carrefour-gcs/shared';
import { z } from 'zod';
import { TreeSelectionZodSchema, type TreePathSegmentType, type TreeSelectionType } from './types/treeSelection.type';
import {
  collectNodeIds,
  countLeaves,
  findByKeys,
  isFullyDisplayed,
  locateInBase,
  nodeIdOfKeys,
  nodeIdOfSelection,
  readOption,
  readTreeNode,
  resolveCheckState
} from './utils/treeNode.utils';

/**
 * Selection lifecycle of a tree filter, over the full option tree of which a search displays a pruned subset.
 */
export class TreeFilterSelection {
  constructor(private readonly options: FilterDefinitionValue[]) {}

  public buildCheckBoxTree(
    displayedOptions: FilterDefinitionValue[],
    selections: TreeSelectionType[],
    opts: { expanded?: boolean } = {}
  ): CheckBoxTreeModelType[] {
    const selectedIds = new Set(selections.map(nodeIdOfSelection));

    return this.buildLevel(displayedOptions, {
      selectedIds,
      parentChecked: false,
      parentKeys: [],
      collapsed: !opts.expanded
    });
  }

  public readSelections(tree: CheckBoxTreeModelType[]): TreeSelectionType[] {
    return this.readLevel(tree, this.options, []);
  }

  public listSelectAllTargets(tree: CheckBoxTreeModelType[]): TreeSelectionType[] {
    return this.listSelectAllLevel(tree, this.options, []);
  }

  /**
   * Splits a selection that straddles the search boundary into its base children, recursively, so
   * that each one is either displayed whole or hidden whole.
   */
  public expandForDisplay(
    selections: TreeSelectionType[],
    displayedOptions: FilterDefinitionValue[]
  ): TreeSelectionType[] {
    return selections.flatMap((selection) => this.expandSelection(selection, displayedOptions));
  }

  public partitionByVisibility(
    selections: TreeSelectionType[],
    tree: CheckBoxTreeModelType[]
  ): { visible: TreeSelectionType[]; hidden: TreeSelectionType[] } {
    const visibleIds = collectNodeIds(tree);
    const isVisible = (selection: TreeSelectionType) => visibleIds.has(nodeIdOfSelection(selection));

    return {
      visible: selections.filter(isVisible),
      hidden: selections.filter((selection) => !isVisible(selection))
    };
  }

  public countSelectedLeaves(selections: TreeSelectionType[]): number {
    return selections.reduce((count, selection) => {
      const node = this.findByPath(selection.path);

      return count + (node ? countLeaves(node) : 0);
    }, 0);
  }

  /**
   * Keeps, from a persisted value, the well-formed selections whose typed node still exists in the option tree.
   */
  public sanitize(raw: unknown): TreeSelectionType[] {
    const parsed = z.array(z.unknown()).safeParse(raw);

    if (!parsed.success) {
      return [];
    }

    return parsed.data
      .flatMap((item) => {
        const selection = TreeSelectionZodSchema.safeParse(item);
        return selection.success ? [selection.data] : [];
      })
      .filter((selection) => Boolean(this.findByPath(selection.path)));
  }

  private findByPath(path: TreePathSegmentType[]): FilterDefinitionValue | undefined {
    let level: FilterDefinitionValue[] | undefined = this.options;
    let node: FilterDefinitionValue | undefined;

    for (const segment of path) {
      node = level?.find((option) => option.value === segment.key && option.type === segment.type);

      if (!node) {
        return undefined;
      }

      level = node.children;
    }

    return node;
  }

  private expandSelection(
    selection: TreeSelectionType,
    displayedOptions: FilterDefinitionValue[]
  ): TreeSelectionType[] {
    const baseNode = this.findByPath(selection.path);
    const displayedNode = findByKeys(displayedOptions, selection.path);

    if (!baseNode || !displayedNode || isFullyDisplayed(displayedNode, baseNode, readOption)) {
      return [selection];
    }

    return (baseNode.children ?? []).flatMap((child) => {
      const type = child.type;

      if (!type || !isFilterTypeGuard(type)) {
        return [];
      }

      return this.expandSelection({ path: [...selection.path, { type, key: child.value }] }, displayedOptions);
    });
  }

  private buildLevel(
    options: FilterDefinitionValue[],
    step: { selectedIds: Set<string>; parentChecked: boolean; parentKeys: string[]; collapsed: boolean }
  ): CheckBoxTreeModelType[] {
    const isRootLevel = step.parentKeys.length === 0;

    return options.map(({ value, label, children }) => {
      const nodeKeys = [...step.parentKeys, value];
      const nodeId = nodeIdOfKeys(nodeKeys);
      const isChecked = step.parentChecked || step.selectedIds.has(nodeId);
      const childNodes = children
        ? this.buildLevel(children, { ...step, parentChecked: isChecked, parentKeys: nodeKeys })
        : undefined;

      return {
        id: nodeId,
        label,
        ...resolveCheckState(isChecked, childNodes),
        children: childNodes,
        collapsed: step.collapsed,
        description: isRootLevel || label.startsWith(value) ? undefined : value
      };
    });
  }

  private readLevel(
    nodes: CheckBoxTreeModelType[],
    baseNodes: FilterDefinitionValue[] | undefined,
    parentPath: TreePathSegmentType[]
  ): TreeSelectionType[] {
    return nodes.flatMap((node) => {
      const located = locateInBase(node, baseNodes, parentPath);

      if (!located) {
        return this.readLevel(node.children ?? [], undefined, parentPath);
      }

      const isFullyChecked = node.checked && !node.indeterminate;

      if (isFullyChecked && isFullyDisplayed(node, located.baseNode, readTreeNode)) {
        return [{ path: located.path }];
      }

      return this.readLevel(node.children ?? [], located.baseNode.children, located.path);
    });
  }

  private listSelectAllLevel(
    nodes: CheckBoxTreeModelType[],
    baseNodes: FilterDefinitionValue[] | undefined,
    parentPath: TreePathSegmentType[]
  ): TreeSelectionType[] {
    return nodes.flatMap((node) => {
      const located = locateInBase(node, baseNodes, parentPath);

      if (!located) {
        return this.listSelectAllLevel(node.children ?? [], undefined, parentPath);
      }

      if (isFullyDisplayed(node, located.baseNode, readTreeNode) || !node.children?.length) {
        return [{ path: located.path }];
      }

      return this.listSelectAllLevel(node.children, located.baseNode.children, located.path);
    });
  }
}
